import { useEffect, useMemo, useState } from "react";
import AttendanceTracker from "../components/AttendanceTracker";
import Notification from "../components/Notification";
import PlacementCard from "../components/PlacementCard";
import TaskCard from "../components/TaskCard";

const TraineeDashboard = ({
  session,
  tasks,
  placements,
  notifications,
  onUpdateTaskStatus,
  attendance,
  onApply,
}) => {
  const [appliedIds, setAppliedIds] = useState(() => {
    try {
      const raw = localStorage.getItem("applied-placements");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [applyMessage, setApplyMessage] = useState("");

  useEffect(() => {
    // keep applied list scoped to trainee sessions
    if (!session || session.role !== "Trainee") {
      setAppliedIds([]);
      return;
    }
  }, [session]);

  const appliedSet = useMemo(() => new Set(appliedIds), [appliedIds]);

  const traineeTasks = tasks.filter((task) => task.traineeId === session?.id);
  const traineeAttendance = attendance.filter((record) => record.userId === session?.id);

  if (!session || session.role !== "Trainee") {
    return (
      <section className="dashboard dashboard-locked">
        <h2>Trainee access required</h2>
        <p>Please login as a trainee to use this dashboard.</p>
      </section>
    );
  }

  const handleApplyPlacement = (placementId) => {
    if (!session) return;
    if (session.role !== "Trainee") {
      setApplyMessage("Only trainees can apply.");
      return;
    }
    if (appliedSet.has(placementId)) {
      setApplyMessage("Already applied to this role.");
      return;
    }
    const updated = [...appliedIds, placementId];
    setAppliedIds(updated);
    localStorage.setItem("applied-placements", JSON.stringify(updated));
    setApplyMessage("Applied successfully.");
  };

  return (
    <section className="dashboard trainee-dashboard">
      <header>
        <p className="eyebrow">Trainee Space</p>
        <h1>Track tasks, stay notified, and follow placements.</h1>
      </header>

      <div className="dashboard-grid">
        <div className="card">
          <h3>Current Tasks</h3>
          <p className="stat">{traineeTasks.length}</p>
        </div>
        <div className="card">
          <h3>Notifications</h3>
          <p className="stat">
            {notifications.filter((note) => note.role === "Trainee" || note.role === "All").length}
          </p>
        </div>
      </div>

      <div className="notification-area">
        <Notification notifications={notifications} role="Trainee" />
      </div>

      <div className="task-list">
        <h3>Assigned Tasks</h3>
        <div className="task-grid">
          {traineeTasks.map((task) => (
            <TaskCard key={task.id} task={task} isEditable onStatusChange={onUpdateTaskStatus} />
          ))}
          {traineeTasks.length === 0 && <p className="muted">No tasks assigned yet.</p>}
        </div>
      </div>

      <section className="placements-panel">
        <h3>Placement Watchlist</h3>
        {applyMessage && <p className="success-callout">{applyMessage}</p>}
        <div className="placement-grid">
          {placements.map((placement) => (
            <PlacementCard
              key={placement.id}
              placement={placement}
              onApply={handleApplyPlacement}
              isApplied={appliedSet.has(placement.id)}
            />
          ))}
        </div>
      </section>

      <AttendanceTracker records={traineeAttendance} title="Your Attendance Log" />
    </section>
  );
};

export default TraineeDashboard;
